/**
 * Screen 7 — Settings. Behind the MANAGER PIN, not the staff one.
 *
 * Holds the price schedule, the walk-in rate, coach assignments, the payout
 * split, both PINs, and the sync connection.
 *
 * Price changes affect FUTURE sales only. Every subscription and walk-in
 * snapshots its price at the moment of sale, so nothing here can rewrite the
 * books — that guarantee is stated on screen, because it is the first thing an
 * owner worries about before touching a price.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  db,
  getSharedSetting,
  setSharedSetting,
  getSetting,
  SETTING_KEYS,
  SHARED_SETTING_KEYS,
} from '../db/db';
import {
  addCoach,
  getCoachRates,
  getSplit,
  getWalkInPrice,
  removeCoach,
  setCoachForClass,
  setCoachRates,
  setScheduledPrice,
  setSplit,
  setWalkInPrice,
} from '../db/operations';
import MoneyInput from '../components/MoneyInput';
import ManagerGate from '../components/ManagerGate';
import { usePendingChangeCount } from '../components/SyncIndicator';
import { runSync } from '../sync/engine';
import { getSupabase, isSupabaseConfigured } from '@shared/supabase';
import { changePin, type PinKind } from '../auth/pin';
import { CLASSES, classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import {
  defaultPriceSchedule,
  describePriceKey,
  MEMBERSHIP_LABELS,
  TERM_MONTHS,
  type MembershipType,
} from '@shared/pricing';
import {
  coachManagerPool,
  isBalancedSplit,
  roundPercent,
  sumPercents,
  MANAGER_COACH_NAME,
  ratesFor,
  withCoachRate,
  type CoachRates,
  type SplitConfig,
} from '@shared/payouts';
import { formatDistanceToNow } from 'date-fns';

export default function Settings() {
  return (
    <ManagerGate>
      <div className="mx-auto max-w-4xl px-5 py-6">
        <h1 className="mb-2 text-4xl text-brand">Settings</h1>
        <p className="mb-6 text-sm text-white/40">
          Manager area. Changes here affect new sales only — past subscriptions and
          walk-ins keep the price they were sold at.
        </p>
        <PriceSchedule />
        <WalkInPrice />
        <Coaches />
        <PrivateCoaches />
        <PayoutSplit />
        <CoachRatesSection />
        <GymName />
        <Pins />
        <SyncSection />
      </div>
    </ManagerGate>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card mb-6 p-6">
      <h2 className="text-xl text-paper">{title}</h2>
      {subtitle && <p className="mb-4 mt-1 text-sm text-white/40">{subtitle}</p>}
      <div className={subtitle ? '' : 'mt-4'}>{children}</div>
    </section>
  );
}

/* -------------------------------------------------------------------------
   Price schedule — the City View card, editable
   ------------------------------------------------------------------------- */

function PriceSchedule() {
  const rows = useLiveQuery(() => db.price_schedule.toArray(), [], []);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<number | null>(null);

  const priceFor = (key: string) =>
    rows.find((r) => r.price_key === key && r.deleted_at === null)?.price_piastres ??
    defaultPriceSchedule().find((e) => e.key === key)?.pricePiastres ??
    0;

  // Rebuild the printed card's layout: four tiers down, member/non-member across.
  const tiers = [
    { label: 'Adult', ageGroup: 'adults', mode: 'group' },
    { label: 'Kids', ageGroup: 'kids', mode: 'group' },
    { label: 'Adult Private', ageGroup: 'adults', mode: 'private' },
    { label: 'Kids Private', ageGroup: 'kids', mode: 'private' },
  ] as const;

  return (
    <Section
      title="Subscription prices"
      subtitle="City View club house schedule. These are TOTALS for the whole term, not per month — a 3-month price already includes its discount."
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-button text-white/40">
              <th className="pb-2 text-left">Type</th>
              {(['club_member', 'non_member'] as MembershipType[]).map((m) => (
                <th key={m} colSpan={3} className="border-l border-edge pb-2 text-center">
                  {MEMBERSHIP_LABELS[m]}
                </th>
              ))}
            </tr>
            <tr className="text-xs text-white/30">
              <th />
              {(['club_member', 'non_member'] as MembershipType[]).map((m) =>
                TERM_MONTHS.map((t) => (
                  <th
                    key={`${m}-${t}`}
                    className={`pb-2 text-center font-normal ${t === 1 ? 'border-l border-edge' : ''}`}
                  >
                    {t}m
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) => (
              <tr key={tier.label} className="border-t border-edge/50">
                <td className="py-2 pr-4 font-semibold">{tier.label}</td>
                {(['club_member', 'non_member'] as MembershipType[]).map((membership) =>
                  TERM_MONTHS.map((term) => {
                    const key = `${tier.ageGroup}_${tier.mode}_${membership}_${term}m`;
                    const value = priceFor(key);
                    return (
                      <td
                        key={key}
                        className={`py-2 text-center ${term === 1 ? 'border-l border-edge' : ''}`}
                      >
                        {editing === key ? (
                          <div className="flex items-center gap-1">
                            <MoneyInput
                              valuePiastres={draft ?? value}
                              onChange={setDraft}
                              className="w-28"
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                if (draft !== null && draft >= 0) {
                                  await setScheduledPrice(key, draft);
                                }
                                setEditing(null);
                                setDraft(null);
                              }}
                              className="rounded-btn bg-brand px-2 py-2 text-xs font-bold"
                            >
                              ✓
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditing(key);
                              setDraft(value);
                            }}
                            className="tabular w-full rounded-btn px-2 py-2 font-semibold
                                       hover:bg-surface hover:text-brand"
                            title={describePriceKey({
                              key,
                              ageGroup: tier.ageGroup,
                              mode: tier.mode,
                              membership,
                              months: term,
                              pricePiastres: value,
                            })}
                          >
                            {formatEgp(value, {
                              showCurrency: false,
                              hideZeroFraction: true,
                            })}
                          </button>
                        )}
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-white/25">Click any price to change it.</p>
    </Section>
  );
}

function WalkInPrice() {
  const price = useLiveQuery(() => getWalkInPrice(), [], null);
  const [draft, setDraft] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  if (price === null) return null;

  return (
    <Section
      title="Walk-in price"
      subtitle="One flat rate for a single session in any class. Past walk-ins keep the price they were charged."
    >
      <div className="flex flex-wrap items-center gap-3">
        <MoneyInput
          valuePiastres={draft ?? price}
          onChange={(p) => {
            setDraft(p);
            setSaved(false);
          }}
          className="w-48"
        />
        <button
          onClick={async () => {
            if (draft !== null && draft >= 0) {
              await setWalkInPrice(draft);
              setSaved(true);
            }
          }}
          disabled={draft === null || draft === price}
          className="btn"
        >
          Save
        </button>
        {saved && <span className="text-sm font-semibold text-status-active">Saved</span>}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------
   Coaches
   ------------------------------------------------------------------------- */

function Coaches() {
  const assignments = useLiveQuery(() => db.coach_assignments.toArray(), [], []);
  // The badge shows the manager's real take on his own class, read from the
  // synced split rather than written in, so it cannot go stale when it moves.
  const split = useLiveQuery(() => getSplit(), [], null);
  const rosterRows = useLiveQuery(() => db.coaches.toArray(), [], []);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const coachFor = (classKey: string) =>
    assignments.find((a) => a.class_key === classKey && a.deleted_at === null)?.coach_name ?? '';

  const known = [
    ...new Set(assignments.map((a) => a.coach_name).filter(Boolean)),
  ].sort();

  // Offer the private-only roster here too, so a coach who starts taking a
  // class does not have to be typed in a second time and misspelled.
  const roster = rosterRows
    .filter((c) => c.deleted_at === null && c.name.trim())
    .map((c) => c.name.trim());

  return (
    <Section
      title="Coaches"
      subtitle="Who teaches each class. This drives the 40% coach share in the payout report — a class with nobody assigned still earns its share, shown as “Unassigned”."
    >
      <ul className="divide-y divide-edge">
        {CLASSES.map((c) => {
          const current = coachFor(c.key);
          const draft = drafts[c.key] ?? current;
          const dirty = draft.trim() !== current.trim();

          return (
            <li key={c.key} className="flex flex-wrap items-center gap-3 py-3">
              <span className="min-w-[200px] flex-1">
                {classLabel(c.key)}
                {c.key === 'kickboxing_adults' && split && (
                  <span className="ml-2 rounded-btn border border-brand/40 bg-brand/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-button text-brand">
                    {coachManagerPool(split)}%
                  </span>
                )}
              </span>

              <input
                className="field w-56 py-2"
                list="coach-names"
                value={draft}
                placeholder="Coach name"
                dir="auto"
                onChange={(e) => setDrafts((d) => ({ ...d, [c.key]: e.target.value }))}
              />
              <button
                onClick={async () => {
                  await setCoachForClass(c.key, draft);
                  setDrafts((d) => {
                    const next = { ...d };
                    delete next[c.key];
                    return next;
                  });
                }}
                disabled={!dirty}
                className="min-h-touch rounded-btn bg-brand px-4 text-sm font-bold uppercase
                           tracking-button disabled:opacity-30"
              >
                Save
              </button>
            </li>
          );
        })}
      </ul>

      <datalist id="coach-names">
        {[...new Set([MANAGER_COACH_NAME, ...known, ...roster])].map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>
    </Section>
  );
}

/* -------------------------------------------------------------------------
   Private-training coaches
   ------------------------------------------------------------------------- */

/**
 * The roster of coaches who are not tied to a class.
 *
 * A class coach exists by being assigned above. Someone who only runs private
 * hours has no class to be assigned to, so without this they could never be
 * named — and their privates would have nowhere to go in the payout report.
 *
 * Both lists feed the private-coach dropdown on a new subscription, and both
 * appear in the payout report even at zero.
 */
function PrivateCoaches() {
  const rows = useLiveQuery(() => db.coaches.toArray(), [], []);
  const assignments = useLiveQuery(() => db.coach_assignments.toArray(), [], []);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const roster = rows
    .filter((c) => c.deleted_at === null && c.name.trim())
    .sort((a, b) => a.name.localeCompare(b.name, ['ar', 'en']));

  const classCoaches = [
    ...new Set(
      assignments
        .filter((a) => a.deleted_at === null && a.coach_name.trim())
        .map((a) => a.coach_name.trim())
    ),
  ].sort((a, b) => a.localeCompare(b, ['ar', 'en']));

  async function add() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError('');
    try {
      await addCoach(trimmed);
      setName('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add that coach');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Private-training coaches"
      subtitle="Extra coaches who take one-to-one sessions. Add anyone here who does not already teach a class — the person running a private hour is often not the class coach, and the payout report pays whoever is named on the subscription."
    >
      <div className="flex flex-wrap items-start gap-3">
        <input
          className="field w-56 py-2"
          value={name}
          placeholder="Coach name"
          dir="auto"
          onChange={(e) => {
            setName(e.target.value);
            setError('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add();
          }}
        />
        <button onClick={add} disabled={busy || !name.trim()} className="btn py-2">
          Add coach
        </button>
      </div>

      {error && <p className="mt-2 text-sm text-status-expired">{error}</p>}

      {roster.length > 0 && (
        <ul className="mt-5 divide-y divide-edge border-t border-edge">
          {roster.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 py-3">
              <span dir="auto">{c.name}</span>
              <button
                onClick={() => removeCoach(c.id)}
                title="Stop offering this coach for new privates. Subscriptions already sold keep their coach."
                className="rounded-btn border border-edge px-3 py-1 text-[10px] font-bold uppercase
                           tracking-button text-white/40 hover:border-status-expired hover:text-status-expired"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {classCoaches.length > 0 && (
        <p className="mt-4 text-xs leading-relaxed text-white/25">
          The private dropdown also offers everyone assigned to a class above:{' '}
          <span className="text-white/40">{classCoaches.join(' · ')}</span>. No need to
          repeat them here.
        </p>
      )}

      {roster.length === 0 && (
        <p className="mt-4 text-sm text-white/30">
          Nobody added yet. Class coaches can already be picked for privates.
        </p>
      )}
    </Section>
  );
}

/* -------------------------------------------------------------------------
   Coach rates
   ------------------------------------------------------------------------- */

/**
 * Per-coach percentages, for the coaches who are not on the default.
 *
 * Every name on the roster is listed with its rate, whether or not it has been
 * changed, because the alternative — a list of exceptions only — makes a
 * misspelled override invisible. If a coach's rate reads 40% when it should
 * read 50%, that is the screen where it is obvious.
 *
 * The extra a coach takes comes out of the MANAGER's share. City View's cut is
 * a venue agreement and does not move, so the coach and manager percentages
 * form a fixed pool and this only decides how it divides.
 */
function CoachRatesSection() {
  const assignments = useLiveQuery(() => db.coach_assignments.toArray(), [], []);
  const rosterRows = useLiveQuery(() => db.coaches.toArray(), [], []);
  const [split, setSplitState] = useState<SplitConfig | null>(null);
  const [rates, setRates] = useState<CoachRates | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      const [s, r] = await Promise.all([getSplit(), getCoachRates()]);
      setSplitState(s);
      setRates(r);
    })();
  }, []);

  // Class coaches and private-only coaches are one list: a rate belongs to a
  // person, not to how they happen to be configured.
  const names = [
    ...new Set([
      ...assignments
        .filter((a) => a.deleted_at === null && a.coach_name.trim())
        .map((a) => a.coach_name.trim()),
      ...rosterRows
        .filter((c) => c.deleted_at === null && c.name.trim())
        .map((c) => c.name.trim()),
      // Names carrying an override but matching nobody on the roster — a
      // rename or a typo. Shown so the mismatch can be seen and cleared.
      ...Object.keys(rates ?? {}),
    ]),
  ]
    .filter((n) => n !== MANAGER_COACH_NAME)
    .sort((a, b) => a.localeCompare(b, ['ar', 'en']));

  if (!split || !rates) return null;

  const pool = coachManagerPool(split);
  const rateFor = (name: string) => ratesFor(name, split, rates).coachPercent;
  const onRoster = new Set([
    ...assignments
      .filter((a) => a.deleted_at === null)
      .map((a) => a.coach_name.trim().toLowerCase()),
    ...rosterRows
      .filter((c) => c.deleted_at === null)
      .map((c) => c.name.trim().toLowerCase()),
  ]);

  return (
    <Section
      title="Coach rates"
      subtitle={`What each coach takes of the revenue they earn. Everyone is on ${split.coachPercent}% unless changed here. The difference comes out of ${MANAGER_COACH_NAME}'s share — City View's ${split.cityViewPercent}% is the same whoever taught the class.`}
    >
      {names.length === 0 ? (
        <p className="text-sm text-white/30">
          No coaches yet. Assign one to a class above, or add a private-training coach.
        </p>
      ) : (
        <ul className="divide-y divide-edge border-y border-edge">
          {names.map((name) => {
            const value = rateFor(name);
            const isDefault = value === split.coachPercent;
            const missing = !onRoster.has(name.trim().toLowerCase());

            return (
              <li key={name} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div>
                  <span dir="auto" className={missing ? 'text-status-expiring' : ''}>
                    {name}
                  </span>
                  {missing && (
                    <span className="ml-2 text-xs text-status-expiring">
                      not on the roster — check the spelling, this rate is being applied to
                      nobody
                    </span>
                  )}
                  {!missing && !isDefault && (
                    <span className="ml-2 rounded-btn border border-status-active/40 bg-status-activeBg px-2 py-0.5 text-[10px] font-bold uppercase tracking-button text-status-active">
                      own rate
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-white/30">
                    {MANAGER_COACH_NAME.split(' ')[0]} takes {sumPercents(pool, -value)}%
                  </span>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      max={pool}
                      step={0.1}
                      value={value}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        setSaved(false);
                        setRates(
                          withCoachRate(
                            rates,
                            name,
                            Number.isFinite(n)
                              ? roundPercent(Math.min(Math.max(0, n), pool))
                              : split.coachPercent
                          )
                        );
                      }}
                      className="field tabular w-24 py-2 pr-7 text-right"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white/30">
                      %
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <p className="mt-4 text-xs leading-relaxed text-white/25">
        {MANAGER_COACH_NAME} is not listed: on the classes and privates he runs himself he
        already takes the coach and manager shares together, {pool}%.
      </p>

      {names.length > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <button
            onClick={async () => {
              await setCoachRates(rates, split.coachPercent);
              setRates(await getCoachRates());
              setSaved(true);
            }}
            className="btn"
          >
            Save rates
          </button>
          {saved && <span className="text-sm font-semibold text-status-active">Saved</span>}
        </div>
      )}
    </Section>
  );
}

/* -------------------------------------------------------------------------
   Payout split
   ------------------------------------------------------------------------- */

function PayoutSplit() {
  const [coach, setCoach] = useState<number | null>(null);
  const [manager, setManager] = useState<number | null>(null);
  const [city, setCity] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void (async () => {
      const split = await getSplit();
      setCoach(split.coachPercent);
      setManager(split.managerPercent);
      setCity(split.cityViewPercent);
    })();
  }, []);

  if (coach === null || manager === null || city === null) return null;

  // Summed in basis points: with a 17.1% share, plain float addition can land
  // a hair off 100 and lock the save button on a split that is correct.
  const total = sumPercents(coach, manager, city);
  const balanced = isBalancedSplit({
    coachPercent: coach,
    managerPercent: manager,
    cityViewPercent: city,
  });

  return (
    <Section
      title="Revenue split"
      subtitle={`How each class's takings are divided. ${MANAGER_COACH_NAME} coaches Kickboxing Adults himself, so on that class he takes the coach and manager shares together.`}
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Percent label="Class coach" value={coach} onChange={setCoach} />
        <Percent label={`Manager (${MANAGER_COACH_NAME})`} value={manager} onChange={setManager} />
        <Percent label="City View branch" value={city} onChange={setCity} />
      </div>

      <div
        className={`mt-4 rounded-btn border p-3 text-sm ${
          balanced
            ? 'border-status-active/40 bg-status-activeBg text-status-active'
            : 'border-status-expired/40 bg-status-expiredBg text-status-expired'
        }`}
      >
        {balanced ? (
          <>
            Totals 100%. Kickboxing Adults pays {MANAGER_COACH_NAME} {sumPercents(coach, manager)}%.
          </>
        ) : (
          <>
            These add up to {total}%, not 100%. Every pound must be allocated — fix this before
            running payouts.
          </>
        )}
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={async () => {
            await setSplit({
              coachPercent: coach,
              managerPercent: manager,
              cityViewPercent: city,
            });
            setSaved(true);
          }}
          disabled={!balanced}
          className="btn"
        >
          Save split
        </button>
        {saved && <span className="text-sm font-semibold text-status-active">Saved</span>}
      </div>
    </Section>
  );
}

function Percent({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <input
          type="number"
          min={0}
          max={100}
          step={0.1}
          className="field tabular pr-10 text-right text-2xl font-bold"
          value={value}
          onChange={(e) =>
            onChange(roundPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0))))
          }
        />
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40">
          %
        </span>
      </div>
    </div>
  );
}

function GymName() {
  const name = useLiveQuery(() => getSharedSetting(SHARED_SETTING_KEYS.gymName), [], null);
  const [draft, setDraft] = useState<string | null>(null);

  if (name === null) return null;
  const value = draft ?? name;

  return (
    <Section title="Gym name" subtitle="Shown on printed receipts.">
      <div className="flex flex-wrap gap-3">
        <input
          className="field flex-1"
          value={value}
          dir="auto"
          onChange={(e) => setDraft(e.target.value)}
        />
        <button
          onClick={() => setSharedSetting(SHARED_SETTING_KEYS.gymName, value.trim())}
          disabled={value.trim() === name}
          className="btn"
        >
          Save
        </button>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------
   PINs
   ------------------------------------------------------------------------- */

function Pins() {
  return (
    <Section
      title="PINs"
      subtitle="The staff PIN unlocks the app. The manager PIN unlocks this screen — do not give it to front desk staff."
    >
      <PinChanger kind="staff" label="Front desk PIN" />
      <div className="mt-6 border-t border-edge pt-6">
        <PinChanger kind="manager" label="Manager PIN" />
      </div>
      <p className="mt-6 text-xs leading-relaxed text-white/25">
        A 4-digit PIN keeps passers-by out of an unattended machine. It is not a password —
        what actually protects the data is the cloud login and its access rules.
      </p>
    </Section>
  );
}

function PinChanger({ kind, label }: { kind: PinKind; label: string }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [message, setMessage] = useState('');
  const [ok, setOk] = useState(false);

  return (
    <div>
      <div className="label mb-2">{label}</div>
      <div className="grid gap-3 sm:grid-cols-3">
        <input
          className="field tabular"
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="Current"
          value={current}
          onChange={(e) => setCurrent(e.target.value.replace(/\D/g, ''))}
        />
        <input
          className="field tabular"
          type="password"
          inputMode="numeric"
          maxLength={4}
          placeholder="New"
          value={next}
          onChange={(e) => setNext(e.target.value.replace(/\D/g, ''))}
        />
        <button
          onClick={async () => {
            try {
              await changePin(current, next, kind);
              setMessage('Changed');
              setOk(true);
              setCurrent('');
              setNext('');
            } catch (e) {
              setMessage(e instanceof Error ? e.message : 'Could not change PIN');
              setOk(false);
            }
          }}
          disabled={current.length !== 4 || next.length !== 4}
          className="btn"
        >
          Change
        </button>
      </div>
      {message && (
        <p className={`mt-2 text-sm ${ok ? 'text-status-active' : 'text-status-expired'}`}>
          {message}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------
   Sync
   ------------------------------------------------------------------------- */

function SyncSection() {
  const lastSynced = useLiveQuery(() => getSetting(SETTING_KEYS.lastSyncedAt), [], null);
  const log = useLiveQuery(
    () => db.sync_log.orderBy('seq').reverse().limit(20).toArray(),
    [],
    []
  );
  const pending = usePendingChangeCount();
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState('');

  return (
    <Section
      title="Sync"
      subtitle="Everything saves on this PC first, then uploads automatically — the moment something is added, and again whenever the internet comes back."
    >
      <CloudSignIn />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-t border-edge pt-4">
        <div className="text-sm text-white/50">
          <div>
            Last successful sync:{' '}
            {lastSynced
              ? formatDistanceToNow(new Date(lastSynced), { addSuffix: true })
              : 'never'}
          </div>
          <div>
            {pending === 0
              ? 'Nothing waiting to upload.'
              : `${pending} change${pending === 1 ? '' : 's'} waiting to upload.`}
          </div>
        </div>
        <button
          onClick={async () => {
            setSyncing(true);
            setResult('');
            const outcome = await runSync('manual');
            setResult(`${outcome.status}: ${outcome.message}`);
            setSyncing(false);
          }}
          disabled={syncing}
          className="btn-ghost"
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {result && <p className="mb-4 text-sm text-white/60">{result}</p>}

      <h3 className="label mb-2">Recent sync attempts</h3>
      {log.length === 0 ? (
        <p className="text-sm text-white/25">No sync attempts recorded yet.</p>
      ) : (
        <ul className="divide-y divide-edge text-sm">
          {log.map((entry) => (
            <li key={entry.seq} className="flex flex-wrap justify-between gap-2 py-2">
              <span className="tabular text-white/40">
                {new Date(entry.started_at).toLocaleString()}
              </span>
              <span
                className={
                  entry.status === 'error'
                    ? 'text-status-expired'
                    : entry.status === 'offline'
                      ? 'text-status-expiring'
                      : entry.status === 'skipped'
                        ? 'text-white/40'
                        : 'text-status-active'
                }
              >
                {entry.status} · ↑{entry.pushed} ↓{entry.pulled}
              </span>
              {entry.status === 'offline' && (
                <span className="w-full text-xs text-status-expiring/70">{entry.message}</span>
              )}
              {entry.status === 'error' && (
                <span className="w-full text-xs text-status-expired/70">{entry.message}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function CloudSignIn() {
  const supabase = getSupabase();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [signedInAs, setSignedInAs] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  /**
   * Which role the signed-in account actually holds, according to the server.
   *
   * This matters because signing in with the WRONG account fails in a way that
   * looks like success: a manager can read, so the pull works and the sync log
   * says `ok`, but every push is refused by Row Level Security. The failure
   * only surfaces later, when someone saves a member and it will not leave the
   * building. Showing the role here turns a silent half-working state into an
   * obvious one.
   */
  const loadRole = useCallback(async () => {
    if (!supabase) return;
    const { data: session } = await supabase.auth.getSession();
    const uid = session.session?.user.id;
    if (!uid) {
      setRole(null);
      return;
    }
    const { data } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', uid)
      .maybeSingle();
    setRole((data?.role as string) ?? 'none');
  }, [supabase]);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSignedInAs(data.session?.user.email ?? null);
      if (data.session) void loadRole();
    });
  }, [supabase, loadRole]);

  if (!isSupabaseConfigured) {
    return (
      <div className="mb-4 rounded-btn border border-edge bg-black/40 p-4 text-sm text-white/50">
        Cloud sync is not configured. The front desk works fully without it — add
        <code className="mx-1 text-brand">VITE_SUPABASE_URL</code> and
        <code className="mx-1 text-brand">VITE_SUPABASE_ANON_KEY</code> to
        <code className="mx-1">.env</code> to switch it on. See SETUP.md.
      </div>
    );
  }

  if (signedInAs) {
    // Anything other than `frontdesk` can read but not write. The app would
    // otherwise appear to work until the first save fails to leave the machine.
    const wrongAccount = role !== null && role !== 'frontdesk';

    return (
      <div className="mb-4">
        <div
          className={`flex flex-wrap items-center justify-between gap-3 rounded-btn border p-4 ${
            wrongAccount
              ? 'border-status-expired/40 bg-status-expiredBg'
              : 'border-status-active/40 bg-status-activeBg'
          }`}
        >
          <span className={`text-sm ${wrongAccount ? 'text-status-expired' : 'text-status-active'}`}>
            Signed in as {signedInAs}
            {role && role !== 'none' && <span className="text-white/50"> · {role}</span>}
          </span>
          <button
            onClick={async () => {
              await supabase?.auth.signOut();
              setSignedInAs(null);
              setRole(null);
            }}
            className="rounded-btn border border-edge px-4 py-2 text-xs font-bold uppercase tracking-button"
          >
            Sign out
          </button>
        </div>

        {wrongAccount && (
          <p className="mt-2 text-sm text-status-expired">
            <strong>This account cannot save anything to the cloud.</strong> It can read, so
            syncing looks like it works, but every member, payment and walk-in entered here
            will stay on this machine and never reach the manager or City View pages. Sign out
            and sign in with the <strong>front desk</strong> account.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <input
          className="field"
          type="email"
          placeholder="Front desk email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          className="field"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          className="btn"
          disabled={busy || !email || !password}
          onClick={async () => {
            setBusy(true);
            setMessage('');
            const { data, error } = await supabase!.auth.signInWithPassword({ email, password });
            if (error) setMessage(error.message);
            else {
              setSignedInAs(data.user?.email ?? email);
              setPassword('');
              await loadRole();
              void runSync('manual');
            }
            setBusy(false);
          }}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </div>
      {message && <p className="mt-2 text-sm text-status-expired">{message}</p>}
    </div>
  );
}
